import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Layers as LayersIcon, Type, Image as ImageIcon, Sticker,
  Plus, Trash2, Play, Save, AlertCircle, Clock, Sparkles, Move3d, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import PublishToCommunityButton from "../components/PublishToCommunityButton";
import { ExportToPlatformButton, PresetSaveLoad } from "../components/WaveTwoFeatures";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECT_OPTIONS = [
  { id: "9:16", label: "9:16 · Reels" },
  { id: "1:1",  label: "1:1 · Square" },
  { id: "16:9", label: "16:9 · Wide" },
  { id: "4:5",  label: "4:5 · Portrait" },
];

const TEXT_DEFAULT_STYLE = {
  color: "#FFFFFF",
  background: null,
  font_size: 64,
  font_weight: "bold",
  italic: false,
  shadow: true,
  stroke: false,
};


const ComposerPage = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [presets, setPresets] = useState({ base_motions: [], layer_motions: [] });
  const [sources, setSources] = useState({ base_candidates: { images: [], videos: [], cgi: [], timelines: [] }, sticker_library: [] });
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      try {
        const [p, s, prj] = await Promise.all([
          axios.get(`${API}/composer/presets`,  { headers: auth }),
          axios.get(`${API}/composer/sources`,  { headers: auth }),
          axios.get(`${API}/composer/projects`, { headers: auth }),
        ]);
        setPresets(p.data || { base_motions: [], layer_motions: [] });
        setSources(s.data || { base_candidates: { images: [], videos: [], cgi: [], timelines: [] }, sticker_library: [] });
        setProjects(prj.data.items || []);
      } catch (e) {
        toast.error("Could not load Composer.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Poll in-flight renders every 6s
  useEffect(() => {
    const id = setInterval(async () => {
      const inFlight = projects.some(p => p.status === "queued" || p.status === "rendering");
      if (!inFlight) return;
      try {
        const r = await axios.get(`${API}/composer/projects`, { headers: auth });
        setProjects(r.data.items || []);
      } catch (_e) { /* poll silently */ }
    }, 6000);
    return () => clearInterval(id);
  }, [projects, auth]);

  const allBases = useMemo(() => {
    const bc = sources.base_candidates || {};
    return [...(bc.videos || []), ...(bc.cgi || []), ...(bc.timelines || []), ...(bc.images || [])];
  }, [sources]);

  const newProject = (baseSrc) => {
    setActiveProject({
      id: null,
      title: "Untitled Composition",
      base_source_collection: baseSrc.collection,
      base_source_id: baseSrc.id,
      _base_preview: baseSrc,
      aspect_ratio: baseSrc.aspect_ratio || "9:16",
      duration_seconds: baseSrc.kind === "video" ? Math.min(baseSrc.duration || 8, 15) : 8,
      base_motion: "none",
      layers: [],
      status: "draft",
    });
  };

  const openProject = async (id) => {
    try {
      const r = await axios.get(`${API}/composer/projects/${id}`, { headers: auth });
      setActiveProject(r.data.project);
    } catch (e) {
      toast.error("Could not open composition.");
    }
  };

  // ----- Layer helpers
  const addTextLayer = () => {
    if (!activeProject) return;
    if ((activeProject.layers?.length || 0) >= 12) {
      toast.error("Max 12 layers per composition.");
      return;
    }
    const layer = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: "text",
      text: "Type here",
      style: { ...TEXT_DEFAULT_STYLE },
      x_pct: 50, y_pct: 50, width_pct: 80, height_pct: 10, rotation_deg: 0,
      start_sec: 0, end_sec: 0,
      fade_in_sec: 0.3, fade_out_sec: 0.3,
      motion: "fade",
    };
    setActiveProject(p => ({ ...p, layers: [...(p.layers || []), layer] }));
  };

  const addStickerLayer = (src) => {
    if (!activeProject) return;
    if ((activeProject.layers?.length || 0) >= 12) {
      toast.error("Max 12 layers per composition.");
      return;
    }
    const layer = {
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type: "sticker",
      source_collection: "content",
      source_id: src.id,
      media_url: src.media_url,
      x_pct: 50, y_pct: 50, width_pct: 30, height_pct: 30, rotation_deg: 0,
      start_sec: 0, end_sec: 0,
      fade_in_sec: 0.3, fade_out_sec: 0.3,
      motion: "fade",
    };
    setActiveProject(p => ({ ...p, layers: [...(p.layers || []), layer] }));
  };

  const updateLayer = (layerId, patch) => {
    setActiveProject(p => ({
      ...p,
      layers: p.layers.map(l => l.id === layerId ? { ...l, ...patch } : l),
    }));
  };

  const updateLayerStyle = (layerId, patch) => {
    setActiveProject(p => ({
      ...p,
      layers: p.layers.map(l => l.id === layerId
        ? { ...l, style: { ...(l.style || TEXT_DEFAULT_STYLE), ...patch } }
        : l),
    }));
  };

  const removeLayer = (layerId) => {
    setActiveProject(p => ({
      ...p,
      layers: p.layers.filter(l => l.id !== layerId),
    }));
  };

  const buildPayload = () => ({
    title: activeProject.title,
    base_source_collection: activeProject.base_source_collection,
    base_source_id: activeProject.base_source_id,
    aspect_ratio: activeProject.aspect_ratio,
    duration_seconds: Number(activeProject.duration_seconds || 8),
    base_motion: activeProject.base_motion || "none",
    layers: (activeProject.layers || []).map(l => {
      const base = {
        type: l.type,
        x_pct: l.x_pct, y_pct: l.y_pct,
        width_pct: l.width_pct, height_pct: l.height_pct,
        rotation_deg: l.rotation_deg,
        start_sec: l.start_sec, end_sec: l.end_sec,
        fade_in_sec: l.fade_in_sec, fade_out_sec: l.fade_out_sec,
        motion: l.motion,
      };
      if (l.type === "text") {
        return { ...base, text: l.text, style: l.style };
      }
      return { ...base, source_collection: "content", source_id: l.source_id };
    }),
  });

  const saveProject = async () => {
    if (!activeProject) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      let r;
      if (activeProject.id) {
        r = await axios.put(`${API}/composer/projects/${activeProject.id}`, payload, { headers: auth });
      } else {
        r = await axios.post(`${API}/composer/projects`, payload, { headers: auth });
      }
      setActiveProject({ ...r.data.project, _base_preview: activeProject._base_preview });
      setProjects(prev => [r.data.project, ...prev.filter(x => x.id !== r.data.project.id)]);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const queueRender = async () => {
    if (!activeProject) return;
    setRendering(true);
    try {
      const payload = buildPayload();
      let proj = activeProject;
      if (activeProject.id) {
        const r = await axios.put(`${API}/composer/projects/${activeProject.id}`, payload, { headers: auth });
        proj = r.data.project;
      } else {
        const r = await axios.post(`${API}/composer/projects`, payload, { headers: auth });
        proj = r.data.project;
      }
      const ok = window.confirm(
        "Rendering composites with text + stickers + camera moves can take a couple of minutes. " +
        "You can safely close this browser tab — we'll email you when it's ready.\n\nContinue?"
      );
      if (!ok) {
        setActiveProject({ ...proj, _base_preview: activeProject._base_preview });
        setRendering(false);
        return;
      }
      const r2 = await axios.post(`${API}/composer/projects/${proj.id}/render`, {}, { headers: auth });
      setActiveProject({ ...r2.data.project, _base_preview: activeProject._base_preview });
      setProjects(prev => [r2.data.project, ...prev.filter(x => x.id !== r2.data.project.id)]);
      toast.success("Render queued 🎬");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue render.");
    } finally {
      setRendering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div
      className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-8 space-y-6 sm:space-y-8"
      data-testid="composer-page"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-500 flex items-center justify-center shadow-md">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-500">
              Composer · AE-Lite · Beta
            </p>
            <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
              Text, stickers, camera moves — composited into one mp4.
            </h1>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          Only XEA-generated media (images, videos, CGI renders, timeline outputs)
          can be used as a base or sticker. Uploads stay disabled — keeps your
          composites 100% copyright-clean.
        </p>
      </div>

      {!activeProject ? (
        <NewCompositionFlow
          allBases={allBases}
          projects={projects}
          onPickBase={newProject}
          onOpenProject={openProject}
        />
      ) : (
        <CompositionEditor
          project={activeProject}
          setProject={setActiveProject}
          presets={presets}
          stickers={sources.sticker_library || []}
          onAddText={addTextLayer}
          onAddSticker={addStickerLayer}
          onUpdateLayer={updateLayer}
          onUpdateLayerStyle={updateLayerStyle}
          onRemoveLayer={removeLayer}
          onSave={saveProject}
          onQueueRender={queueRender}
          onBack={() => setActiveProject(null)}
          saving={saving}
          rendering={rendering}
        />
      )}
    </div>
  );
};


/* ───────────────────────────────── New comp flow ──────────────────────── */

const NewCompositionFlow = ({ allBases, projects, onPickBase, onOpenProject }) => (
  <div className="space-y-8">
    {/* Existing projects */}
    {projects.length > 0 && (
      <section data-testid="composer-project-list">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
          Your compositions
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map(p => {
            const inFlight = p.status === "queued" || p.status === "rendering";
            const pct = Math.max(0, Math.min(100, Number(p.progress || 0)));
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onOpenProject(p.id)}
                data-testid={`composer-project-${p.id}`}
                className="text-left bg-white border border-ink-200 rounded-2xl p-4 hover:border-ink-950 transition-all"
              >
                <p className="text-base font-bold text-ink-950 truncate">{p.title}</p>
                <p className="text-xs text-ink-500 mt-1">
                  {(p.layers || []).length} layers · {Number(p.duration_seconds || 0).toFixed(0)}s · {p.aspect_ratio}
                </p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <span className={`inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full ${
                    p.status === "completed" ? "bg-emerald-100 text-emerald-700"
                    : p.status === "failed"  ? "bg-rose-100 text-rose-700"
                    : inFlight                ? "bg-amber-100 text-amber-700"
                                             : "bg-ink-100 text-ink-600"
                  }`}>
                    {p.status}
                  </span>
                  {inFlight && (
                    <span data-testid={`composer-project-${p.id}-pct`} className="text-[11px] font-bold text-blue-500 tabular-nums">
                      {pct}%
                    </span>
                  )}
                  {p.result_url && (
                    <a href={p.result_url} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="text-[10px] font-bold text-blue-500 underline">
                      view mp4
                    </a>
                  )}
                </div>
                {inFlight && (
                  <div className="mt-2 flex flex-col gap-1" data-testid={`composer-project-${p.id}-progress`}>
                    <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      {p.progress_label && (
                        <span className="text-[10px] text-ink-500 truncate">{p.progress_label}</span>
                      )}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm("Cancel this render? It can't be resumed.")) return;
                          try {
                            await axios.post(`${API}/composer/projects/${p.id}/cancel`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                            window.location.reload();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Could not cancel.");
                          }
                        }}
                        data-testid={`composer-project-${p.id}-cancel`}
                        className="text-[10px] font-bold text-rose-500 hover:text-rose-700 inline-flex items-center gap-0.5 ml-auto"
                      >
                        <X className="w-3 h-3" /> cancel
                      </button>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>
    )}

    {/* Base picker */}
    <section>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-3">
        Start a new composition · Pick a base clip
      </p>
      {allBases.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-ink-200 p-8 text-center bg-white text-sm text-ink-500">
          You need at least one XEA generation (Marketing / Designer / CGI / Timeline)
          before you can start a composition.
        </div>
      ) : (
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 gap-2">
          {allBases.slice(0, 32).map(s => (
            <button
              key={`${s.collection}:${s.id}`}
              type="button"
              onClick={() => onPickBase(s)}
              data-testid={`composer-base-${s.id}`}
              className="relative rounded-2xl overflow-hidden border-2 aspect-square bg-ink-100 border-ink-200 hover:border-ink-950 transition-all group"
            >
              {s.kind === "video" ? (
                <video src={s.media_url} muted playsInline preload="metadata"
                       className="w-full h-full object-cover" />
              ) : (
                <img src={s.media_url} alt="" loading="lazy"
                     className="w-full h-full object-cover" />
              )}
              <span className="absolute top-1 left-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-black/70 text-white">
                {s.kind === "video" ? "vid" : "img"}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  </div>
);


/* ───────────────────────────────── Editor ─────────────────────────────── */

const CompositionEditor = ({
  project, setProject, presets, stickers,
  onAddText, onAddSticker, onUpdateLayer, onUpdateLayerStyle, onRemoveLayer,
  onSave, onQueueRender, onBack, saving, rendering,
}) => {
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const selected = (project.layers || []).find(l => l.id === selectedLayerId) || null;

  const updateField = (field, value) => setProject(p => ({ ...p, [field]: value }));

  const baseUrl = project._base_preview?.media_url || project.base?.media_url;
  const baseKind = project._base_preview?.kind || project.base?.kind || "image";

  return (
    <div className="space-y-6" data-testid="composer-editor">
      {/* Title + meta row */}
      <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={onBack}
                  data-testid="composer-back"
                  className="text-xs font-bold text-ink-500 hover:text-ink-950">
            ← Compositions
          </button>
          <input
            type="text"
            value={project.title}
            onChange={(e) => updateField("title", e.target.value)}
            maxLength={120}
            data-testid="composer-title"
            className="flex-1 min-w-[200px] text-xl font-bold text-ink-950 bg-transparent outline-none border-b border-transparent focus:border-blue-500"
          />
          <span className="text-xs text-ink-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {Number(project.duration_seconds || 0).toFixed(0)}s · {project.aspect_ratio}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">Canvas</p>
            <div className="flex flex-wrap gap-1.5">
              {ASPECT_OPTIONS.map(a => (
                <button key={a.id} type="button"
                        onClick={() => updateField("aspect_ratio", a.id)}
                        data-testid={`composer-aspect-${a.id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                          project.aspect_ratio === a.id
                            ? "bg-blue-500 text-white border-blue-500"
                            : "bg-white border-ink-200"
                        }`}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2">Duration (s)</p>
            <input type="number" min="1" max="60" step="1"
                   value={project.duration_seconds}
                   onChange={(e) => updateField("duration_seconds", Math.max(1, Math.min(60, Number(e.target.value))))}
                   data-testid="composer-duration"
                   className="w-full bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200 text-sm" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-2 flex items-center gap-1">
              <Move3d className="w-3 h-3" /> Camera move
            </p>
            <select value={project.base_motion}
                    onChange={(e) => updateField("base_motion", e.target.value)}
                    data-testid="composer-camera-move"
                    className="w-full bg-ink-50 rounded px-2 py-1 outline-none focus:bg-white border border-ink-200 text-sm">
              {(presets.base_motions || []).map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <div className="mt-2">
              <PresetSaveLoad
                scope="camera"
                currentPayload={{ base_motion: project.base_motion }}
                onApply={(p) => updateField("base_motion", p.base_motion || "none")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Live preview canvas */}
      <PreviewCanvas
        project={project}
        baseUrl={baseUrl}
        baseKind={baseKind}
        selectedLayerId={selectedLayerId}
        onSelectLayer={setSelectedLayerId}
      />

      {/* Layer toolbar */}
      <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mr-auto">
            Layers ({(project.layers || []).length}/12)
          </p>
          <Button type="button" variant="outline" onClick={onAddText}
                  data-testid="composer-add-text"
                  className="text-xs">
            <Type className="w-3.5 h-3.5 mr-1" /> Add text
          </Button>
        </div>

        {/* Sticker quick-pick row */}
        {stickers.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-400 mb-2">
              Sticker library — click to drop on canvas
            </p>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {stickers.slice(0, 16).map(s => (
                <button key={s.id} type="button"
                        onClick={() => onAddSticker(s)}
                        data-testid={`composer-sticker-${s.id}`}
                        className="shrink-0 w-14 h-14 rounded-xl overflow-hidden border-2 border-ink-200 hover:border-blue-500">
                  <img src={s.media_url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}

        {(project.layers || []).length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">
            Add a text layer or click a sticker above to drop your first layer.
          </div>
        ) : (
          <div className="space-y-2" data-testid="composer-layers-list">
            {project.layers.map((l, idx) => (
              <LayerRow
                key={l.id}
                layer={l}
                index={idx}
                selected={selectedLayerId === l.id}
                onSelect={() => setSelectedLayerId(l.id === selectedLayerId ? null : l.id)}
                onRemove={() => {
                  onRemoveLayer(l.id);
                  if (selectedLayerId === l.id) setSelectedLayerId(null);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inspector */}
      {selected && (
        <LayerInspector
          layer={selected}
          presets={presets}
          maxDuration={Number(project.duration_seconds || 8)}
          onUpdate={(patch) => onUpdateLayer(selected.id, patch)}
          onUpdateStyle={(patch) => onUpdateLayerStyle(selected.id, patch)}
        />
      )}

      {/* Action bar */}
      <div className="sticky bottom-2 z-10">
        <div className="bg-ink-950 text-white rounded-3xl px-5 py-4 flex flex-wrap items-center justify-between gap-3 shadow-xl">
          <p className="text-sm">
            <strong>{(project.layers || []).length}</strong> layers
            <span className="opacity-50 mx-2">·</span>
            <strong>{Number(project.duration_seconds || 0).toFixed(0)}s</strong>
            <span className="opacity-50 mx-2">·</span>
            <span className="opacity-70">{project.aspect_ratio} · {project.base_motion}</span>
            {project.status && project.status !== "draft" && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-white/10">
                {project.status}
              </span>
            )}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onSave} disabled={saving}
                    data-testid="composer-save"
                    className="text-white hover:bg-white/10">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
            <Button onClick={onQueueRender} disabled={rendering}
                    data-testid="composer-render"
                    className="bg-blue-500 hover:bg-blue-600 text-white">
              {rendering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              Render mp4
            </Button>
          </div>
        </div>
      </div>

      {project.result_url && project.status === "completed" && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-bold text-emerald-900">Your composition is ready 🎬</p>
          <div className="flex gap-2 items-center flex-wrap">
            <a href={project.result_url} target="_blank" rel="noopener noreferrer"
               data-testid="composer-result-link"
               className="text-sm font-bold text-emerald-700 underline">Open mp4 →</a>
            {project.id && (
              <PublishToCommunityButton
                sourceCollection="composer_projects"
                sourceId={project.id}
                defaultCaption={project.title}
                testIdPrefix="composer-publish"
              />
            )}
            {project.id && (
              <ExportToPlatformButton
                sourceCollection="composer_projects"
                sourceId={project.id}
                testIdPrefix="composer-export"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};


/* ───────────────────────────────── Preview canvas ─────────────────────── */

const PreviewCanvas = ({ project, baseUrl, baseKind, selectedLayerId, onSelectLayer }) => {
  // Approximate visual preview using CSS positioning — actual render happens
  // server-side via ffmpeg.
  const ar = project.aspect_ratio || "9:16";
  const [w, h] = ar.split(":").map(Number);
  const ratio = (h / w) * 100;

  return (
    <div className="bg-ink-950 rounded-3xl p-4 flex justify-center">
      <div
        data-testid="composer-preview"
        className="relative bg-black overflow-hidden rounded-2xl shadow-2xl"
        style={{
          width: ar === "16:9" ? "min(100%, 720px)" : "min(360px, 100%)",
          aspectRatio: `${w} / ${h}`,
          paddingBottom: undefined,
        }}
      >
        {baseUrl ? (
          baseKind === "video" ? (
            <video src={baseUrl} muted playsInline loop autoPlay
                   className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <img src={baseUrl} alt="" loading="lazy"
                 className="absolute inset-0 w-full h-full object-cover" />
          )
        ) : null}

        {/* Layer ghosts — purely visual, not pixel-perfect */}
        {(project.layers || []).map((l, idx) => (
          <button
            key={l.id}
            type="button"
            onClick={() => onSelectLayer(l.id)}
            data-testid={`composer-layer-ghost-${idx}`}
            className={`absolute transition-all ${
              selectedLayerId === l.id ? "ring-2 ring-blue-400" : ""
            }`}
            style={{
              left: `${l.x_pct}%`,
              top: `${l.y_pct}%`,
              transform: `translate(-50%, -50%) rotate(${l.rotation_deg || 0}deg)`,
              width: l.type === "sticker" ? `${l.width_pct}%` : "auto",
              maxWidth: "90%",
            }}
          >
            {l.type === "text" ? (
              <span
                className="block whitespace-nowrap font-black tracking-tight px-2"
                style={{
                  color: l.style?.color || "#FFFFFF",
                  fontSize: `${Math.max(10, (l.style?.font_size || 48) / 6)}px`,
                  textShadow: l.style?.shadow ? "0 2px 6px rgba(0,0,0,0.6)" : undefined,
                  WebkitTextStroke: l.style?.stroke ? "1px black" : undefined,
                  background: l.style?.background || "transparent",
                  borderRadius: 4,
                }}
              >
                {l.text}
              </span>
            ) : (
              <img
                src={l.media_url}
                alt=""
                className="w-full h-auto"
                draggable={false}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};


/* ───────────────────────────────── Layer row ──────────────────────────── */

const LayerRow = ({ layer, index, selected, onSelect, onRemove }) => {
  const Icon = layer.type === "text" ? Type : layer.type === "logo" ? ImageIcon : Sticker;
  const label = layer.type === "text"
    ? (layer.text || "Text").slice(0, 32)
    : layer.type;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      data-testid={`composer-layer-${index}`}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border text-left cursor-pointer ${
        selected ? "bg-blue-500/10 border-blue-500" : "bg-white border-ink-200 hover:border-ink-950"
      }`}
    >
      <span className="text-[10px] font-black text-ink-500 w-5 text-center">{index + 1}</span>
      <Icon className="w-4 h-4 text-ink-500 shrink-0" />
      <span className="flex-1 text-sm font-bold text-ink-950 truncate">{label}</span>
      <span className="text-[10px] text-ink-400 tabular-nums">
        {Number(layer.start_sec).toFixed(1)}s → {Number(layer.end_sec || 0).toFixed(1) || "end"}
      </span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        data-testid={`composer-layer-${index}-remove`}
        className="text-rose-500 hover:text-rose-700 p-1"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};


/* ───────────────────────────────── Inspector ──────────────────────────── */

const LayerInspector = ({ layer, presets, maxDuration, onUpdate, onUpdateStyle }) => (
  <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4" data-testid="composer-inspector">
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
      Layer settings
    </p>

    {layer.type === "text" && (
      <>
        <input
          type="text"
          value={layer.text || ""}
          onChange={(e) => onUpdate({ text: e.target.value })}
          maxLength={200}
          data-testid="composer-text-input"
          placeholder="Type your text..."
          className="w-full p-3 rounded-xl border border-ink-200 focus:outline-none focus:border-blue-500 text-base font-bold"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Color</span>
            <input type="color"
                   value={layer.style?.color || "#FFFFFF"}
                   onChange={(e) => onUpdateStyle({ color: e.target.value })}
                   data-testid="composer-text-color"
                   className="h-8 rounded" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Font size</span>
            <input type="number" min="12" max="240" step="2"
                   value={layer.style?.font_size || 48}
                   onChange={(e) => onUpdateStyle({ font_size: Number(e.target.value) })}
                   data-testid="composer-text-size"
                   className="bg-ink-50 rounded px-2 py-1 border border-ink-200" />
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox"
                   checked={!!layer.style?.shadow}
                   onChange={(e) => onUpdateStyle({ shadow: e.target.checked })}
                   data-testid="composer-text-shadow" />
            <span>Shadow</span>
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox"
                   checked={!!layer.style?.stroke}
                   onChange={(e) => onUpdateStyle({ stroke: e.target.checked })}
                   data-testid="composer-text-stroke" />
            <span>Stroke</span>
          </label>
        </div>
      </>
    )}

    {/* Position + size */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">X (%)</span>
        <input type="range" min="0" max="100" step="1"
               value={layer.x_pct}
               onChange={(e) => onUpdate({ x_pct: Number(e.target.value) })}
               data-testid="composer-layer-x" />
        <span className="text-[10px] text-ink-500 tabular-nums">{Math.round(layer.x_pct)}%</span>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Y (%)</span>
        <input type="range" min="0" max="100" step="1"
               value={layer.y_pct}
               onChange={(e) => onUpdate({ y_pct: Number(e.target.value) })}
               data-testid="composer-layer-y" />
        <span className="text-[10px] text-ink-500 tabular-nums">{Math.round(layer.y_pct)}%</span>
      </label>
      {layer.type !== "text" && (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Width (%)</span>
            <input type="range" min="5" max="100" step="1"
                   value={layer.width_pct}
                   onChange={(e) => onUpdate({ width_pct: Number(e.target.value), height_pct: Number(e.target.value) })}
                   data-testid="composer-layer-size" />
            <span className="text-[10px] text-ink-500 tabular-nums">{Math.round(layer.width_pct)}%</span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Rotation</span>
            <input type="range" min="-180" max="180" step="2"
                   value={layer.rotation_deg}
                   onChange={(e) => onUpdate({ rotation_deg: Number(e.target.value) })}
                   data-testid="composer-layer-rot" />
            <span className="text-[10px] text-ink-500 tabular-nums">{Math.round(layer.rotation_deg)}°</span>
          </label>
        </>
      )}
    </div>

    {/* Timing + motion */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Start (s)</span>
        <input type="number" min="0" max={maxDuration} step="0.1"
               value={layer.start_sec}
               onChange={(e) => onUpdate({ start_sec: Math.max(0, Math.min(maxDuration, Number(e.target.value))) })}
               data-testid="composer-layer-start"
               className="bg-ink-50 rounded px-2 py-1 border border-ink-200" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">End (s · 0 = end)</span>
        <input type="number" min="0" max={maxDuration} step="0.1"
               value={layer.end_sec}
               onChange={(e) => onUpdate({ end_sec: Math.max(0, Math.min(maxDuration, Number(e.target.value))) })}
               data-testid="composer-layer-end"
               className="bg-ink-50 rounded px-2 py-1 border border-ink-200" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Fade in</span>
        <input type="number" min="0" max="5" step="0.1"
               value={layer.fade_in_sec}
               onChange={(e) => onUpdate({ fade_in_sec: Math.max(0, Math.min(5, Number(e.target.value))) })}
               data-testid="composer-layer-fadein"
               className="bg-ink-50 rounded px-2 py-1 border border-ink-200" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-ink-400 font-bold">Fade out</span>
        <input type="number" min="0" max="5" step="0.1"
               value={layer.fade_out_sec}
               onChange={(e) => onUpdate({ fade_out_sec: Math.max(0, Math.min(5, Number(e.target.value))) })}
               data-testid="composer-layer-fadeout"
               className="bg-ink-50 rounded px-2 py-1 border border-ink-200" />
      </label>
    </div>

    <div>
      <p className="text-[9px] uppercase tracking-wider text-ink-400 font-bold mb-2">Layer motion</p>
      <div className="flex flex-wrap gap-1.5">
        {(presets.layer_motions || []).map(m => (
          <button key={m.id} type="button"
                  onClick={() => onUpdate({ motion: m.id })}
                  data-testid={`composer-layer-motion-${m.id}`}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                    layer.motion === m.id
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-white border-ink-200"
                  }`}>
            {m.label}
          </button>
        ))}
      </div>
    </div>

    <div className="pt-3 border-t border-ink-100">
      <PresetSaveLoad
        scope="layer"
        currentPayload={{
          type: layer.type,
          style: layer.style,
          width_pct: layer.width_pct,
          height_pct: layer.height_pct,
          rotation_deg: layer.rotation_deg,
          fade_in_sec: layer.fade_in_sec,
          fade_out_sec: layer.fade_out_sec,
          motion: layer.motion,
        }}
        onApply={(p) => {
          const patch = {};
          ["width_pct","height_pct","rotation_deg","fade_in_sec","fade_out_sec","motion"]
            .forEach(k => { if (p[k] !== undefined) patch[k] = p[k]; });
          onUpdate(patch);
          if (layer.type === "text" && p.style) {
            onUpdateStyle(p.style);
          }
        }}
      />
    </div>
  </div>
);


export default ComposerPage;
